import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { SellingPoint } from '../../../components/custom';
import { Button, ProgressBar } from '../../../components/general';
import {
  PageWrapper,
  PageHeader,
  BackBtn,
} from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { URL } from '../../../constants';

import { getTemplateData } from '../../../services/api/template';
import { getAnalysisData } from '../../../services/videoBuilderService';

import { useProjectStore } from '../../../stores/project';

import {
  HlReel,
  Shot,
  AnalysisClips,
  PageError,
  ModalError,
} from '../../../types';
import { isEmpty, getUrl } from '../../../utils';

import DoneBtn from '../components/doneBtn';
import { getAnalyseText } from '../components/getText';
import ClipsBlocks from './components/clipsBlocks';
import HlReelPreviewContainer from './components/hlReelPreviewContainer';

const Analysis = () => {
  const { eventId = '' } = useParams();

  const {
    eventId: eventIdLocal,
    currentProject,
    projectError,
    setEventId,
  } = useProjectStore();

  const [isAnalysing, setIsAnalysing] = useState(false);

  // people
  const [peopleCount, setPeopleCount] = useState(0);

  // highlight reel
  const [hlReel, setHlReel] = useState<HlReel | null>(null);
  const [requiredShots, setRequiredShots] = useState<Shot[]>([]);
  const [hasShowedHlReelToast, setHasShowedHlReelToast] = useState(false);

  // clips
  const [clips, setClips] = useState<AnalysisClips | null>(null);
  const [hasShowedClipsToast, setHasShowedClipsToast] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState<ModalError>(null);

  const navigate = useNavigate();

  // set event id on mount
  useEffect(() => {
    if (eventIdLocal !== eventId) setEventId(eventId);
  }, [eventId, eventIdLocal, setEventId]);

  const fetchData = useCallback(
    async (didMount?: boolean) => {
      try {
        const {
          peopleCount,
          hlReel: hlReelAnalysis,
          isHlReelAnalysed,
          clips,
          isClipsAnalysed,
          isAnalysed,
          error,
        } = await getAnalysisData({ eventId });

        // if some assets are missing – show error
        if (error) {
          setError(getAssetsError(error));
          return;
        }

        // update state (it re-fetches every 10 seconds when analysing)
        setIsAnalysing(!isAnalysed);

        // PEOPLE
        setPeopleCount(peopleCount);

        // HIGHLIGHT REEL
        // if highlight reel is processed – update state & fetch reel data
        if (isHlReelAnalysed) {
          if (isEmpty(hlReel) || isEmpty(requiredShots)) {
            const { hlReel, requiredShots } = await getTemplateData(eventId);

            setHlReel({
              ...hlReel,
              thumbnailUrl: hlReelAnalysis?.thumbnailUrl,
            });
            setRequiredShots(requiredShots);
          }

          // if page didn't just mount – show success toast
          if (!hasShowedHlReelToast) {
            if (!didMount)
              toast.success('The highlight reel is processed!', {
                toastId: 'highlight-reel-processing-success',
              });

            setHasShowedHlReelToast(true);
          }
        }

        // CLIPS
        setClips(clips);

        // if page didn't just mount – show success toast
        if (isClipsAnalysed && !hasShowedClipsToast) {
          if (!didMount)
            toast.success('All clips are processed!', {
              toastId: 'clips-processing-success',
            });

          setHasShowedClipsToast(true);
        }
      } catch (err) {
        console.error(`Error fetching analysis data: ${err}`);
        setError({ subheading: 'Error fetching analysis data' });
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventId, hlReel, requiredShots, hasShowedClipsToast, hasShowedHlReelToast],
  );

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchData(true);

      setIsLoading(false);
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
      setIsAnalysing(false);
      return;
    }

    if (currentProject) onMount();
  }, [currentProject, projectError, fetchData]);

  useEffect(() => {
    if (isLoading) return;

    // if analysing – re-fetch every 10 seconds
    if (isAnalysing) {
      const interval = setInterval(async () => await fetchData(), 10000);
      return () => clearInterval(interval);
    }

    // when finished analysing – redirect to Review page
    else navigate(getUrl(URL.VB_REVIEW, eventId));
  }, [eventId, isLoading, isAnalysing, fetchData, navigate]);

  const getAssetsError = (type: string) => {
    const errorsDict: Record<string, { text: string; url: string }> = {
      'no-hl-reel': { text: 'highlight reel', url: URL.VB_HL_REEL },
      'no-clips': { text: 'clips', url: URL.VB_CLIPS },
      'no-people': { text: 'people', url: URL.VB_PEOPLE },
    };

    return {
      heading: `No ${errorsDict[type].text} found`,
      subheading: `Please return to previous step and upload the ${errorsDict[type].text}`,
      btn: (
        <Button
          className="w-full capitalize"
          onClick={() => navigate(getUrl(errorsDict[type].url, eventId))}
        >
          Upload {errorsDict[type].text}
        </Button>
      ),
    };
  };

  const { analyseText, progress } = getAnalyseText({
    hlReel,
    clips,
    peopleCount,
  });

  return (
    <PageWrapper
      type="project"
      title={`Analysis – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
    >
      <BackBtn label="Video Builder" />

      <div className="flex flex-col gap-8">
        <PageHeader heading="Analysing" />

        {!error && (
          <React.Fragment>
            <SellingPoint text={analyseText} />

            <div className="flex flex-col gap-4 text-center">
              <ProgressBar progress={progress} />

              <div className="font-bold uppercase">Analysing</div>
            </div>

            <ClipsBlocks
              clips={clips}
              fetchData={fetchData}
              peopleCount={peopleCount}
              isShowSuccessfulClips
            />

            <HlReelPreviewContainer
              hlReel={hlReel}
              requiredShots={requiredShots}
              showVideo
            />

            <DoneBtn
              isBack
              subheading="Don't worry, we'll continue analysing"
            />
          </React.Fragment>
        )}
      </div>

      <ErrorModal
        show={!!error}
        heading={error?.heading ?? 'Something went wrong'}
        subheading={error?.subheading}
        btn={error?.btn}
        isTryAgainBtn={!error?.btn}
      />
    </PageWrapper>
  );
};

export default Analysis;
